import { useContext } from 'react';
import { UserContext } from '../context/UserContext';
import { Line } from '../types';
import { apiClient } from '../utils/apiClient';
import { API_CONFIG } from '../config/apiConfig';

interface UpsertLinesParams {
  returnHeaderId: string;
  lines: Line[];
  status?: string;
}

interface UpsertLinesResponse {
  success: boolean;
  return_status: string;
  return_message: string;
}

export const useUpsertLinesService = () => {
  const { userId } = useContext(UserContext);

  const constructLineJson = (line: Line, headerIdParam: string, statusOverride?: string): any => {
    const currentDate = new Date().toISOString();
    const isInsert = !line.returnLineId || (typeof line.returnLineId === 'string' && line.returnLineId.startsWith('new-'));

    const baseData = {
      operation: isInsert ? 'INSERT' : 'UPDATE',
      itemNumber: line.validatedSerialItem || line.itemNumber || '',
      validatedSerialNumber: line.validatedSerialNumber,
      validatedAssetNumber: line.validatedAssetNumber,
      toSubInventory: line.toSubinventory,
      toLocator: line.toLocator,
      pallet: line.pallet || line.toPallet,
      cage: line.cage || line.toCage,
      tote: line.tote || line.toTote,
      repairEligibility: line.repairEligibility === 'Yes' ? 'Y' : 'N',
      radReceiptDate: currentDate,
      lastUpdateDate: currentDate,
      lastUpdatedBy: userId
    };

    if (isInsert) {
      return {
        ...baseData,
        returnLineId: null,
        returnHeaderId: line.returnHeaderId || headerIdParam,
        description: line.description,
        quantity: line.quantity || 1,
        uom: line.uom || 'EA',
        serialNumber: line.serialNumber,
        creationDate: currentDate,
        createdBy: userId,
        assetNumber: line.assetNumber,
        status: statusOverride || line.status || 'New',
        rmaOrArma: line.rmaOrArma || 'RMA',
        trackingNumber: line.trackingNumber
      };
    } else {
      return {
        ...baseData,
        returnLineId: line.returnLineId,
        returnHeaderId: line.returnHeaderId,
        description: line.description,
        trackingNumber: line.trackingNumber,
        status: statusOverride || line.status
      };
    }
  };

  const upsertLines = async ({ returnHeaderId, lines, status }: UpsertLinesParams): Promise<UpsertLinesResponse> => {
    const modifiedLines = lines.filter(line => 
      (line.returnLineId && !line.returnLineId.toString().startsWith('new-') && line.validatedSerialNumber) ||  // UPDATE: has real returnLineId AND validatedSerialNumber
      !line.returnLineId || line.returnLineId.toString().startsWith('new-')                                    // INSERT: no returnLineId or new- prefixed
    );

    if (modifiedLines.length === 0) {
      throw new Error('No modified lines to save');
    }

    const linesJson = modifiedLines.map(line => constructLineJson(line, returnHeaderId, status));

    // Find the first UPDATE operation to get returnLineId
    const updateLine = modifiedLines.find(line => line.returnLineId);
    
    const payload = {
      p_return_header_id: returnHeaderId,
      p_return_line_id: updateLine?.returnLineId || null,
      p_lines_json: JSON.stringify(linesJson)
    };

    const response = await apiClient.post(API_CONFIG.ENDPOINTS.UPSERT_LINE_DETAILS, payload);

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to save lines');
    }

    return response.data;
  };

  return {
    upsertLines
  };
};